import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import MultipleValueDropdownInput from "./MultipleValueDropdownInput";
import type { DropdownInputOption } from "types/DropdownInputOption";
import { getCurrentSchoolYear } from "utilities/CurrentSchoolYear";
import {
  buildClassOptions,
  getClassesForAcademicYearsCached,
  getClassesForAcademicYearsCachedSnapshot,
  mergeClassOptions,
} from "services/classOptionServices";

interface ClassMultipleValueDropdownInputProps {
  classId: string[];
  disable?: boolean;
  onChange: (classId: string[]) => void;
  selectedClassOptions?: DropdownInputOption[];
}

const EMPTY_CLASS_OPTIONS: DropdownInputOption[] = [];

const areClassOptionsEqual = (
  leftOptions: DropdownInputOption[],
  rightOptions: DropdownInputOption[],
) => {
  if (leftOptions.length !== rightOptions.length) return false;

  return leftOptions.every(
    (leftOption, index) =>
      leftOption.value === rightOptions[index].value &&
      leftOption.label === rightOptions[index].label,
  );
};

const ClassMultipleValueDropdownInput: React.FC<
  ClassMultipleValueDropdownInputProps
> = ({
  classId,
  disable = false,
  onChange,
  selectedClassOptions,
}) => {
  const { t } = useTranslation();
  const currentYear = getCurrentSchoolYear();
  const normalizedSelectedClassOptions =
    selectedClassOptions || EMPTY_CLASS_OPTIONS;
  const targetYears = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1],
    [currentYear],
  );
  const initialClassOptions = useMemo(
    () =>
      mergeClassOptions(
        [
          buildClassOptions(
            getClassesForAcademicYearsCachedSnapshot(targetYears),
            "asc",
          ),
          normalizedSelectedClassOptions,
        ],
        "asc",
      ),
    [normalizedSelectedClassOptions, targetYears],
  );
  const hasInitialClassOptions = initialClassOptions.length > 0;
  const [classOptions, setClassOptions] =
    useState<DropdownInputOption[]>(initialClassOptions);
  const [isLoading, setIsLoading] = useState(initialClassOptions.length === 0);

  useEffect(() => {
    setClassOptions((currentOptions) => {
      const mergedOptions = mergeClassOptions(
        [currentOptions, normalizedSelectedClassOptions],
        "asc",
      );

      return areClassOptionsEqual(currentOptions, mergedOptions)
        ? currentOptions
        : mergedOptions;
    });
  }, [normalizedSelectedClassOptions]);

  useEffect(() => {
    let isMounted = true;

    const fetchClasses = async () => {
      if (!hasInitialClassOptions) {
        setIsLoading(true);
      }

      try {
        const classes = await getClassesForAcademicYearsCached(targetYears);
        if (!isMounted) return;

        const fetchedOptions = buildClassOptions(classes, "asc");
        setClassOptions((currentOptions) => {
          const mergedOptions = mergeClassOptions(
            [
              fetchedOptions,
              currentOptions,
              normalizedSelectedClassOptions,
            ],
            "asc",
          );

          return areClassOptionsEqual(currentOptions, mergedOptions)
            ? currentOptions
            : mergedOptions;
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchClasses();

    return () => {
      isMounted = false;
    };
  }, [hasInitialClassOptions, normalizedSelectedClassOptions, targetYears]);

  return (
    <div className="w-full">
      <MultipleValueDropdownInput
        placeholder={t("_account_form._plese_select_class")}
        options={classOptions}
        value={classId}
        disable={disable}
        isLoading={isLoading}
        loadingMessage={t("_generic._loading")}
        emptyMessage={t("_generic._no_data")}
        onChange={(value) => onChange(value)}
        maxHeight="400px"
      />
    </div>
  );
};

export default ClassMultipleValueDropdownInput;
